import { createRoot } from 'react-dom/client'
import * as Sentry from '@sentry/react'
import App from './App.tsx'
import './index.css'
import { recordError } from './lib/lastErrorTracker'
import { startDataUsageMonitoring } from './lib/dataUsageTracker'
import { getVersion } from './utils/version'

// App version banner — first thing in the console on every page load.
console.log(
  `%c Life Movie Maker ${getVersion()} `,
  'background:linear-gradient(to right,#fb923c,#ec4899,#9333ea);color:#fff;font-weight:bold;padding:2px 6px;border-radius:4px',
);

// Debug mode: localStorage.setItem('DEBUG_VIDEO_PLAYER', 'true')

// Sentry DSN is a public client-side identifier — safe to hardcode.
// It only grants permission to SEND events, not read them.
// See: https://docs.sentry.io/concepts/key-terms/dsn-explainer/
const SENTRY_DSN = "https://15ba03b1e005fba51f766db317abe1de@o4510734233829376.ingest.de.sentry.io/4510734284816464";

// Disable Sentry on local dev ports. To force-enable locally for testing,
// run: localStorage.setItem('SENTRY_LOCAL', 'true') then reload.
const isLocalDev = ['5173', '5174', '8080', '8081', '8082', '8083', '8084'].includes(window.location.port);
const forceLocalSentry = localStorage.getItem('SENTRY_LOCAL') === 'true';
const sentryDisabled = isLocalDev && !forceLocalSentry;

// Initialize Sentry BEFORE rendering app
Sentry.init({
  dsn: sentryDisabled ? undefined : SENTRY_DSN,

  // Environment
  environment: import.meta.env.MODE, // 'development' or 'production'

  // Release tracking (set in build pipeline)
  release: import.meta.env.VITE_APP_VERSION,

  // Integrations
  integrations: [
    Sentry.browserTracingIntegration(),

    Sentry.replayIntegration({
      blockAllMedia: false,
      maskAllText: false,
      maskAllInputs: false,
    }),

    // User Feedback — we render our own dialog and call Sentry.captureFeedback()
    Sentry.feedbackIntegration({
      autoInject: false,
      showBranding: false,
    }),
  ],

  // Performance Monitoring
  tracesSampleRate: 1.0,

  // Session Replay sample rates.
  // 10% of sessions are recorded continuously; 100% of sessions that hit
  // an error are recorded retroactively. Feedback submissions attach the
  // most recent replay segment regardless of sampling.
  replaysSessionSampleRate: 0.1,
  replaysOnErrorSampleRate: 1.0,

  // Send ALL errors (including localhost). Also record the event ID so a
  // feedback submission in the next 2 minutes can link to this error.
  beforeSend(event) {
    if (event.event_id && event.type !== 'feedback') {
      recordError(event.event_id);
    }
    return event;
  },

  // Ignore specific errors
  ignoreErrors: [
    // Browser extension errors
    'top.GLOBALS',
    // Network errors (user has bad connection)
    'NetworkError',
    'Failed to fetch',
    // Aborted video playback (user-initiated)
    'AbortError: The play() request was interrupted',
  ],
});

// Phase 0 client data-usage monitoring (local diagnostic; inert for normal users)
startDataUsageMonitoring();

createRoot(document.getElementById("root")!).render(<App />);
